import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Music, 
  Upload, 
  Bell, 
  User, 
  ChevronLeft, 
  ChevronRight,
  FileStack,
  ShieldCheck,
  ExternalLink,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import SongManager from './components/SongManager';
import SongAssembler from './components/SongAssembler';
import AuditLogTable from './components/AuditLogTable';
import DashboardStats from './components/DashboardStats';
import RecentActivityChart from './components/RecentActivityChart';
import TopSongsChart from './components/TopSongsChart';
import FileUploader from './components/FileUploader';
import LoginModal from './components/LoginModal';
import { jwtDecode } from "jwt-decode";

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  // Initialize based on screen width: Open on Desktop, Closed on Mobile
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserProfile(decoded);
      } catch (e) {
        console.error("Invalid token", e);
        handleLogout();
      }
    }
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('rehearsal_password');
    setToken(null);
    setUserProfile(null);
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'songs', label: 'Song Manager', icon: Music },
    { id: 'service-builder', label: 'Merge Scores', icon: FileStack },
    { id: 'audit', label: 'Audit Logs', icon: ShieldCheck },
    { id: 'uploads', label: 'Upload new scores or songs', icon: Upload },
  ];

  if (!token) {
    return <LoginModal onLoginSuccess={(t) => setToken(t)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans text-gray-900 selection:bg-blue-100 selection:text-blue-900">
      
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed h-full z-40 bg-white border-r border-gray-100 transition-all duration-300 flex flex-col shadow-[4px_0_24px_-12px_rgba(0,0,0,0.1)]
          ${isSidebarOpen 
            ? 'translate-x-0 w-72' 
            : '-translate-x-full w-72 md:translate-x-0 md:w-24'
          }
        `}
      >
        <div className="h-24 flex items-center px-8 justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-blue-200 shadow-lg mr-4 shrink-0 transition-transform hover:scale-105">
              <Music size={20} strokeWidth={3} />
            </div>
            <span 
              className={`font-extrabold text-xl tracking-tight whitespace-nowrap overflow-hidden transition-all duration-500 ${
                isSidebarOpen ? 'opacity-100 w-auto' : 'opacity-0 w-0 absolute'
              }`}
            >
              bChoir<span className="text-blue-600">Admin</span>
            </span>
          </div>
          {/* Mobile Close Button */}
          <button 
            onClick={() => setIsSidebarOpen(false)}
            className="md:hidden text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (!item.disabled) {
                    setActiveTab(item.id);
                    // Close sidebar on mobile when clicking an item
                    if (window.innerWidth < 768) setIsSidebarOpen(false);
                  }
                }}
                disabled={item.disabled}
                className={`w-full flex items-center p-4 rounded-2xl transition-all duration-300 group relative ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' 
                    : 'text-gray-500 hover:bg-blue-50 hover:text-blue-700'
                } ${item.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <Icon 
                  size={22} 
                  className={`shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'} ${isSidebarOpen ? 'mr-4' : 'mx-auto'}`} 
                  strokeWidth={isActive ? 2.5 : 2}
                />
                
                <span 
                  className={`font-bold whitespace-nowrap overflow-hidden transition-all duration-500 ${
                    isSidebarOpen ? 'opacity-100 w-auto' : 'opacity-0 w-0 absolute'
                  }`}
                >
                  {item.label}
                </span>

                {!isSidebarOpen && (
                  <div className="absolute left-full ml-4 px-3 py-2 bg-gray-900 text-white text-xs font-bold rounded-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50 whitespace-nowrap shadow-xl translate-x-2 group-hover:translate-x-0 hidden md:block">
                    {item.label}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-6 border-t border-gray-50 space-y-4">
          <button 
            onClick={handleLogout}
            className={`w-full flex items-center p-4 text-red-500 hover:bg-red-50 rounded-2xl transition-all duration-300 group ${!isSidebarOpen && 'justify-center'}`}
          >
            <LogOut size={22} className={`${isSidebarOpen && 'mr-4'} transition-transform group-hover:scale-110`} />
            {isSidebarOpen && <span className="font-bold">Log Out</span>}
          </button>
          
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-full hidden md:flex items-center justify-center p-3 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all duration-300"
          >
            {isSidebarOpen ? <ChevronLeft size={24} /> : <ChevronRight size={24} />}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${
          isSidebarOpen ? 'md:ml-72' : 'md:ml-24'
        } ml-0`}
      >
        {/* Glassmorphic Header */}
        <header className="h-20 sm:h-24 bg-white/80 backdrop-blur-md border-b border-gray-100 px-6 sm:px-10 flex justify-between items-center sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="md:hidden p-2 -ml-2 text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <Menu size={24} />
            </button>
            
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 capitalize tracking-tight animate-slide-up">
                {activeTab.replace('-', ' ')}
              </h1>
              <p className="text-sm text-gray-500 font-medium animate-fade-in animation-delay-100 hidden sm:block">
                Welcome back, {userProfile?.name || 'Administrator'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 sm:space-x-8">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-blue-50 text-blue-600 rounded-xl font-bold text-sm hover:bg-blue-100 transition-colors group"
            >
              <span className="hidden sm:inline">Public Portal</span>
              <span className="sm:hidden">Public</span>
              <ExternalLink size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>

            <div className="relative group cursor-pointer hidden sm:block">
              <div className="p-2 text-gray-400 group-hover:text-blue-600 transition-colors">
                <Bell size={24} />
                <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 sm:border-l sm:pl-8 border-gray-200">
              <div className="text-right hidden md:block">
                <p className="text-sm font-bold text-gray-900">{userProfile?.name || 'Admin User'}</p>
                <p className="text-xs text-gray-500 font-medium truncate max-w-[150px]">{userProfile?.email || 'Super Admin'}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-tr from-blue-100 to-blue-50 border-2 border-white shadow-md flex items-center justify-center text-blue-600 overflow-hidden">
                {userProfile?.picture ? (
                  <img src={userProfile.picture} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User size={20} className="sm:w-6 sm:h-6" />
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 sm:p-10 max-w-[1600px] mx-auto w-full animate-slide-up animation-delay-200">
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              <div className="flex flex-col w-full">
                <DashboardStats />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Hymns Chart */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                  <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="font-bold text-lg text-gray-900">Hymns Sung (last 2 months)</h3>
                  </div>
                  <div className="p-6 flex-1">
                    <RecentActivityChart />
                  </div>
                </div>

                 {/* Top Songs Chart */}
                 <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                  <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="font-bold text-lg text-gray-900">Top 5 Songs</h3>
                  </div>
                  <div className="p-6 flex-1">
                    <TopSongsChart />
                  </div>
                </div>

                {/* Audit Log */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col lg:col-span-2">
                  <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="font-bold text-lg text-gray-900">System Activity</h3>
                  </div>
                  <div className="p-2 flex-1">
                    <AuditLogTable limit={5} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'songs' && <SongManager />}

          {activeTab === 'audit' && (
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-100 bg-gray-50/50">
                <h3 className="font-bold text-lg text-gray-900">System Audit Logs</h3>
              </div>
              <div className="p-2">
                <AuditLogTable />
              </div>
            </div>
          )}

          {activeTab === 'service-builder' && <SongAssembler />}
          
          {activeTab === 'uploads' && <FileUploader />}
        </div>
      </main>
    </div>
  );
}

export default App;
// Trigger production build
